import { useEffect, useLayoutEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

/**
 * サイドバーの幅を取得するhooks
 * CSS変数 --sidebar-width から値を取得します
 *
 * - 電話 (sm 未満 / <600): ドロワー扱いのため 0
 * - タブレット (600〜1199): 縮小レール固定の 60
 * - PC (lg 以上 / >=1200): CSS変数 --sidebar-width から取得 (60 or 210)
 *
 * requestAnimationFrame は使わない。初回ペイント前に確定値へ揃えないと、
 * 左オフセット用 transition が「右からスライドイン」に見える。
 */
export const getResponsiveSidebarWidth = (
  viewportWidth: number,
  phoneBreakpoint: number,
  desktopBreakpoint: number,
): number => {
  if (viewportWidth < phoneBreakpoint) return 0;
  if (viewportWidth < desktopBreakpoint) return 60;
  return 210;
};

export const useSidebarWidth = (): number => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 210;
    return getResponsiveSidebarWidth(
      window.innerWidth,
      theme.breakpoints.values.sm,
      theme.breakpoints.values.lg,
    );
  });

  // 電話=0 / タブレット=60 は確定値のため、同期更新する
  useLayoutEffect(() => {
    if (isPhone) {
      setSidebarWidth(0);
    } else if (isTablet) {
      setSidebarWidth(60);
    }
  }, [isPhone, isTablet]);

  useEffect(() => {
    // PC のみ CSS変数を監視する
    if (isPhone || isTablet) {
      return;
    }

    const updateSidebarWidth = () => {
      const width = getComputedStyle(document.documentElement)
        .getPropertyValue("--sidebar-width")
        .trim();
      if (width) {
        const widthValue = parseFloat(width.replace("px", ""));
        if (!isNaN(widthValue)) {
          setSidebarWidth(widthValue);
        }
      }
    };

    updateSidebarWidth();

    const observer = new MutationObserver(updateSidebarWidth);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      observer.disconnect();
    };
  }, [isPhone, isTablet]);

  return sidebarWidth;
};
